export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    // Raw response body text on a non-OK response (e.g. a 422's validation message). Never
    // rendered directly — pt-BR-only is a hard rule — but useful on the error object for
    // debugging when the client and server disagree about a rule.
    public detail?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ErrorInfo {
  title: string;
  description: string;
}

export function getErrorMessage(error: unknown): ErrorInfo {
  if (error instanceof ApiError) {
    return getApiErrorMessage(error.status, error.code);
  }

  if (error != null && typeof error === 'object' && 'name' in error && error.name === 'ZodError') {
    return {
      title: 'Resposta inesperada do servidor',
      description: 'Os dados retornados não estão no formato esperado.',
    };
  }

  if (error instanceof Error) {
    return {
      title: 'Erro',
      description: error.message || 'Ocorreu um erro inesperado.',
    };
  }

  return {
    title: 'Erro',
    description: 'Ocorreu um erro inesperado. Tente novamente.',
  };
}

export function getApiErrorMessage(status?: number, code?: string): ErrorInfo {
  if (code) {
    const codeMessages: Record<string, ErrorInfo> = {
      'auth/user-not-found': {
        title: 'Usuário não encontrado',
        description: 'Não encontramos uma conta com este e-mail.',
      },
      'auth/invalid-email': {
        title: 'E-mail inválido',
        description: 'Por favor, insira um e-mail válido.',
      },
      'auth/wrong-password': {
        title: 'Senha incorreta',
        description: 'A senha informada está incorreta.',
      },
      'auth/too-many-requests': {
        title: 'Muitas tentativas',
        description: 'Muitas tentativas. Tente novamente mais tarde.',
      },
      'auth/email-already-in-use': {
        title: 'E-mail já cadastrado',
        description: 'Este e-mail já está sendo usado por outra conta.',
      },
      'auth/weak-password': {
        title: 'Senha fraca',
        description: 'A senha deve ter pelo menos 6 caracteres.',
      },
    };

    if (codeMessages[code]) {
      return codeMessages[code];
    }
  }

  if (status) {
    const statusMessages: Record<number, ErrorInfo> = {
      400: {
        title: 'Requisição inválida',
        description: 'Os dados enviados são inválidos. Verifique e tente novamente.',
      },
      401: {
        title: 'Não autorizado',
        description: 'Sua sessão expirou. Faça login novamente.',
      },
      403: {
        title: 'Acesso negado',
        description: 'Você não tem permissão para realizar esta ação.',
      },
      404: {
        title: 'Não encontrado',
        description: 'O recurso solicitado não foi encontrado.',
      },
      409: {
        title: 'Conflito',
        description: 'Este registro já existe ou há um conflito com os dados.',
      },
      422: {
        title: 'Dados inválidos',
        description: 'Os dados enviados não puderam ser processados.',
      },
      429: {
        title: 'Muitas requisições',
        description: 'Você fez muitas requisições. Aguarde um momento.',
      },
      500: {
        title: 'Erro do servidor',
        description: 'Ocorreu um erro no servidor. Tente novamente mais tarde.',
      },
      502: {
        title: 'Serviço indisponível',
        description: 'O serviço está temporariamente indisponível.',
      },
      503: {
        title: 'Serviço indisponível',
        description: 'O serviço está em manutenção. Tente novamente mais tarde.',
      },
    };

    if (statusMessages[status]) {
      return statusMessages[status];
    }
  }

  return {
    title: 'Erro',
    description: 'Ocorreu um erro inesperado. Tente novamente.',
  };
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true;
  }
  return false;
}

export function getNetworkErrorMessage(): ErrorInfo {
  return {
    title: 'Erro de conexão',
    description: 'Não foi possível conectar ao servidor. Verifique sua conexão.',
  };
}
